// Features: M004-F0715, M004-F0720
// Tests: T-M004-0210, T-M004-0225
//
// ============================================================================
// The ONE place the move / Advice-amount / bill-by-hand wire lives (Spec core-ledger-account-gateway 1.4.0,
// digest fef4ca18..., Spec main 8e34a411). Kept out of ledgerQueuesAdapter.js on purpose so the lanes that touch
// that file do not collide; the page consumes the three request builders and normaliseMove().
//
//   moveLedgerTransaction (transaction.move) POST .../transactions/{handOffId}/move  {to, reason?}
//        ledger_manage, SAL 4, Idempotency-Key required. Ledger-local: it NEVER involves a Core read (design 4.1a).
//   editLedgerDraftLine   (draft.line.edit)  POST .../draft/lines
//        action advice_amount {advice_id, amount_minor, reason}                    (ACCT-TX-019)
//        action add_manual    {description, amount_minor, reason, for_hand_off_id} (ACCT-TX-014, F0720)
//
// Every request built here is validated by Spec's generated client in the tests (tests/ledger-account-gateway).
// ============================================================================
import { errorCode, normaliseAdvice, normaliseDraft, toMinor } from './ledgerQueuesAdapter';
import { displayTxId } from '../utils/transactionId';

const base = (orgId, userId) => `/v1/orgs/${encodeURIComponent(orgId)}/ledger/users/${encodeURIComponent(userId)}`;
const withKey = (key) => ({ headers: { 'Idempotency-Key': key } });

export function buildLedgerMovesApi(http) {
  return {
    // `to` is 'draft' | 'advice'. `reason` is sent ONLY when given: an unsettled / duplicate item answers 200 `moved:false`
    // with `reason_required` until the manager supplies one (ACCT-TX-004/005/017).
    moveTransaction: (orgId, userId, { handOffId, to, reason }, idempotencyKey) => http.post(
      `${base(orgId, userId)}/transactions/${encodeURIComponent(handOffId)}/move`,
      reason ? { to, reason } : { to },
      withKey(idempotencyKey),
    ),
    // ACCT-TX-019: the NEW advice_id-keyed amount edit. Reason is REQUIRED by the contract and by this builder (no default text).
    editAdviceAmount: (orgId, userId, { adviceId, amount, reason }, idempotencyKey) => http.post(
      `${base(orgId, userId)}/draft/lines`,
      { action: 'advice_amount', advice_id: adviceId, amount_minor: toMinor(amount), reason },
      withKey(idempotencyKey),
    ),
    // ACCT-TX-014 / 016: the manual line JOINS the TX named by for_hand_off_id. `correction` (an upward correction on a
    // WRITTEN TX) is sent only together with for_hand_off_id and only when true.
    billByHand: (orgId, userId, { handOffId, description, amount, reason, correction }, idempotencyKey) => {
      const body = { action: 'add_manual', description, amount_minor: toMinor(amount), reason, for_hand_off_id: handOffId };
      if (correction === true) body.correction = true;
      return http.post(`${base(orgId, userId)}/draft/lines`, body, withKey(idempotencyKey));
    },
  };
}

const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body) return body.data;
  return body ?? null;
};

// TxCondition: Core's / the Ledger's sentences are carried VERBATIM (ACCT-TX-011), never composed.
export const normaliseConditions = (list) => (Array.isArray(list) ? list : [])
  .filter((condition) => condition && typeof condition.code === 'string' && condition.code)
  .map((condition) => ({
    code: condition.code,
    message: typeof condition.message === 'string' ? condition.message : '',
    nextAction: typeof condition.next_action === 'string' ? condition.next_action : '',
    fixable: condition.fixable === true,
  }));

// The queue state carried by the answers of a move and of draft.line.edit: both hold `draft` (null = no open draft) and `advice`.
export function normaliseQueueState(data, money) {
  const body = data && typeof data === 'object' ? data : {};
  return {
    advice: normaliseAdvice({ data: { items: Array.isArray(body.advice) ? body.advice : [], currency: body.currency } }),
    draft: normaliseDraft({ data: { draft: body.draft ?? null } }, money),
  };
}

// transaction.move 200 (Spec TransactionMoveData). `moved:false` with an empty `conditions` is a legitimate repeat (never an error);
// `moved:false` WITH conditions means "not done yet, here is what to fix" (never an error either).
export function normaliseMove(response, money) {
  const body = unwrap(response) || {};
  return {
    moved: body.moved === true,
    handOffId: typeof body.hand_off_id === 'string' ? body.hand_off_id : '',
    txId: typeof body.tx_id === 'string' ? body.tx_id : '',
    fromStage: typeof body.from_stage === 'string' ? body.from_stage : '',
    toStage: typeof body.to_stage === 'string' ? body.to_stage : '',
    conditions: normaliseConditions(body.conditions),
    ...normaliseQueueState(body, money),
  };
}

// draft.line.edit 200 (Spec DraftLineEditData): draft + advice.
export const normaliseEdit = (response, money) => normaliseQueueState(unwrap(response) || {}, money);

export const hasCondition = (conditions, code) => conditions.some((condition) => condition.code === code);

// ---- Refusal copy (design 4.4, 4.6, 4.7a). Declared codes only; anything else falls back to the node's own words. ----
const txSuffix = (handOffId, txnId) => { const txId = displayTxId({ txnId, handOffId }); return txId ? ` (${txId})` : ''; };
export const MOVE_FALLBACK = 'The move could not be saved. Nothing was moved.';
export const EDIT_FALLBACK = 'The change could not be saved. Nothing was changed.';
export const HAND_FALLBACK = 'This could not be added to the next invoice. Nothing was changed.';
const bodyMessage = (error) => {
  const body = error?.response?.data || {};
  return (typeof body.message === 'string' && body.message.trim() ? body.message : '') || (typeof body.error?.message === 'string' ? body.error.message : '');
};

export function moveRefusal(error, handOffId) {
  const code = errorCode(error);
  const tx = txSuffix(handOffId);
  if (code === 'LEDGER_TX_WRITTEN') {
    return { text: `This cost has been written to the ledger, so it can't be moved. To change it, raise a credit note; the correction is recorded under the same transaction${tx}.`, reload: true };
  }
  if (code === 'LEDGER_HANDOFF_NOT_FOUND') {
    const next = error?.response?.data?.next_action;
    return { text: `The Ledger has no record of this cost yet: Core has not sent it.${typeof next === 'string' && next.trim() ? ` ${next}` : ''}`, reload: true };
  }
  if (code === 'LEDGER_HA_IDEMPOTENCY_CONFLICT') return { text: 'This move was already sent with different details. Check the page and try again.', reload: true };
  return { text: bodyMessage(error) || MOVE_FALLBACK, reload: false };
}

export function amountRefusal(error, handOffId) {
  const code = errorCode(error);
  if (code === 'LEDGER_HANDOFF_IMMUTABLE' || code === 'LEDGER_TX_WRITTEN') {
    return { text: `This cost has been written to the ledger, so its amount can't be changed. To change it, raise a credit note; the correction is recorded under the same transaction${txSuffix(handOffId)}.`, reload: true };
  }
  if (code === 'LEDGER_ADVICE_NOT_FOUND') return { text: 'That cost is no longer in Advice. The view has been refreshed.', reload: true };
  if (code === 'LEDGER_HA_IDEMPOTENCY_CONFLICT') return { text: 'This change was already sent with different details. Check the page and try again.', reload: true };
  // Spec core-ledger-account-gateway 1.28.0 (ML001-F0343, Ledger T-LED-APPT-024): a part line amount comes from the billing-profile rule.
  if (code === 'LEDGER_PART_AMOUNT_DERIVED') return { text: "This amount is worked out from the bill-part rule, so it can't be edited here.", reload: false };
  return { text: bodyMessage(error) || EDIT_FALLBACK, reload: false };
}

export function handRefusal(error, handOffId) {
  const code = errorCode(error);
  if (code === 'LEDGER_HANDOFF_IMMUTABLE' || code === 'LEDGER_TX_WRITTEN') {
    return { text: 'This cost has already been written to the ledger, so it can\'t be billed by hand. Corrections are made with a credit note.', reload: true };
  }
  if (code === 'LEDGER_VAT_REQUIRED') {
    return { text: 'A VAT rate is needed for this organisation before this can be added. Ask an admin to set the organisation’s VAT rate, then try again.', reload: false };
  }
  if (code === 'LEDGER_HA_IDEMPOTENCY_CONFLICT') return { text: 'This was already sent with different details. Check the page and try again.', reload: true };
  return { text: bodyMessage(error) || HAND_FALLBACK, reload: false };
}

// ---- Pending (Core #393). The Ledger can be slower than Core's 3 s tunnel SLA. Core answers a same-key re-entry with
// 202 {status:'pending', code:'LEDGER_COMMAND_IN_PROGRESS', hand_off_id, retry_after_seconds} while the first command is
// still in flight, and the FIRST request may get 504 TUNNEL_NODE_TIMEOUT although the Ledger applies the move. Neither is
// a refusal: the page waits, refreshes the queue and never sends the command again (the key makes a later re-send safe, but
// the button stays disabled while the answer is pending). Spec core-ledger-account-gateway 1.12.0 declares neither answer.
export const PENDING_CODE = 'LEDGER_COMMAND_IN_PROGRESS';
export const TUNNEL_TIMEOUT_CODE = 'TUNNEL_NODE_TIMEOUT';
export const DEFAULT_RETRY_SECONDS = 5;
export function pendingAnswer(response) {
  if (Number(response?.status) !== 202) return null;
  const body = response?.data;
  const data = body && typeof body === 'object' && !Array.isArray(body) && 'data' in body ? body.data : body;
  if (!data || typeof data !== 'object' || data.status !== 'pending' || data.code !== PENDING_CODE) return null;
  const seconds = Number(data.retry_after_seconds);
  return { handOffId: typeof data.hand_off_id === 'string' ? data.hand_off_id : '', retryAfterSeconds: Number.isFinite(seconds) && seconds > 0 ? seconds : DEFAULT_RETRY_SECONDS };
}
export const isTunnelTimeout = (error) => Number(error?.response?.status) === 504 && errorCode(error) === TUNNEL_TIMEOUT_CODE;
